/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

// ─── Status badge helper ──────────────────────────────────────────────────────
// completedpercent: "0"=pending, "1"=in-progress, "3"=completed, "4"=failed
const getStatusBadge = (completedpercent) => {
  switch (completedpercent) {
    case "0": return { color: "bg-secondary", label: "Pending" };
    case "1": return { color: "bg-primary",   label: "In Progress" };
    case "3": return { color: "bg-success",   label: "Completed" };
    case "4": return { color: "bg-danger",    label: "Failed" };
    default:  return { color: "bg-secondary", label: "Pending" };
  }
};

// ─── Payload preview builder ──────────────────────────────────────────────────
const buildPayloadPreview = (automationConfig, isReactive, moderatorEmail, apiBaseUrl) => {
  if (!automationConfig) return null;

  const extra =
    automationConfig.payload_template &&
    typeof automationConfig.payload_template === "object" &&
    Object.keys(automationConfig.payload_template).length > 0
      ? automationConfig.payload_template
      : {};

  if (isReactive) {
    return {
      callbackUrl: `${apiBaseUrl}/automationtaskschedule/webhook/n8n/callback`,
      taskid: "{{taskid}}",
      email: "{{email}}",
      name: "{{name}}",
      phone: "{{phone}}",
      token: automationConfig.token || "",
      webhook_name: automationConfig.webhook_name || "",
      ...extra,
    };
  }

  return {
    taskid: "{{taskid}}",
    email: "{{email}}",
    name: "{{name}}",
    phone: "{{phone}}",
    ...extra,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// AutomationTaskDetailPopup
// ─────────────────────────────────────────────────────────────────────────────
const AutomationTaskDetailPopup = ({ taskId, isOpen, onClose }) => {
  const userToken = localStorage.getItem("token");
  const { t } = useTranslation();

  const [isLoading, setIsLoading]       = useState(false);
  const [task, setTask]                 = useState(null);
  const [childTasks, setChildTasks]     = useState([]);
  const [searchQuery, setSearchQuery]   = useState("");

  useEffect(() => {
    if (isOpen && taskId) fetchDetails();
  }, [isOpen, taskId]);

  const fetchDetails = async () => {
    setIsLoading(true);
    setTask(null);
    setChildTasks([]);
    try {
      const resp = await getData(
        `${url.API_BASE_URL}${url.API_GET_AUTOMATION_TASK_SCHEDULE_DETAILS}/${taskId}?token=${userToken}`
      );
      if (resp.status && resp.data) {
        setTask(resp.data);
        setChildTasks(resp.data.childtask || []);
      }
    } catch (err) {
      console.error("Error fetching task details:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setTask(null);
    setChildTasks([]);
    setSearchQuery("");
    onClose();
  };

  const isReactive = task?.executiontype === "reactive";

  // Automation config is identical on all child tasks – grab the first one that has a URL
  const automationConfig =
    childTasks.find((ct) => ct.automation?.webhook_url)?.automation || null;

  // Proactive API endpoint URL (built once, reused in JSX)
  const proactiveUrl = `${url.API_BASE_URL}/automationtaskschedule/proactive?email=${task?.moderator?.email || "{user_email}"}`;

  const previewJson = (() => {
    const preview = buildPayloadPreview(
      automationConfig,
      isReactive,
      task?.moderator?.email,
      url.API_BASE_URL
    );
    return preview ? JSON.stringify(preview, null, 2) : null;
  })();

  const filteredChildTasks = childTasks.filter((ct) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      String(ct.customid || "").includes(q) ||
      (ct.leadid?.name    || "").toLowerCase().includes(q) ||
      (ct.leadid?.surname || "").toLowerCase().includes(q) ||
      (ct.leadid?.email   || "").toLowerCase().includes(q) ||
      (ct.leadid?.company || "").toLowerCase().includes(q)
    );
  });

  if (!isOpen) return null;

  return (
    <>
      {/* ── Offcanvas panel ── */}
      <div
        className="offcanvas offcanvas-end bg-white border-0 shadow-lg show"
        style={{ visibility: "visible", width: "80vw", maxWidth: "960px" }}
        tabIndex="-1"
        aria-labelledby="offcanvasTaskDetailLabel"
      >
        {/* ── Header ── */}
        <div className="offcanvas-header bg-primary text-white p-4 border-bottom">
          <div className="d-flex align-items-center gap-3">
            <div
              className="p-2 rounded-circle d-flex align-items-center justify-content-center"
              style={{ background: "rgba(255,255,255,0.15)" }}
            >
              <span className="material-symbols-outlined text-white" style={{ fontSize: "22px" }}>
                task_alt
              </span>
            </div>
            <div>
              <h5
                className="offcanvas-title fw-bold text-white mb-1"
                id="offcanvasTaskDetailLabel"
              >
                {task?.title || t("Task Details")}
              </h5>
              <div className="d-flex align-items-center gap-2">
                {task?.executiontype && (
                  <span
                    className={`badge ${
                      isReactive ? "bg-info text-dark" : "bg-warning text-dark"
                    }`}
                  >
                    {isReactive ? t("Reactive") : t("Proactive")}
                  </span>
                )}
                <small className="text-white opacity-75">
                  {childTasks.length} {t("subtasks")}
                </small>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="btn-close btn-close-white"
            onClick={handleClose}
            aria-label="Close"
          />
        </div>

        {/* ── Body ── */}
        <div className="offcanvas-body p-4" style={{ overflowY: "auto" }}>
          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t("Loading...")}</span>
              </div>
              <p className="mt-3 text-muted">{t("Loading task details...")}</p>
            </div>
          ) : task ? (
            <div>

              {/* ── 1. Parent Task Info ── */}
              <div className="mb-4 p-4 rounded-3 border border-primary border-opacity-25 shadow-sm bg-light">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="material-symbols-outlined text-primary" style={{ fontSize: "20px" }}>
                    account_tree
                  </span>
                  <h6 className="mb-0 fw-bold text-dark">{t("Parent Task")}</h6>
                </div>

                <h5 className="fw-bold text-dark mb-2">{task.title}</h5>
                {task.description && (
                  <p className="text-muted mb-3" style={{ fontSize: "14px", lineHeight: "1.6" }}>
                    {task.description}
                  </p>
                )}

                <div className="row g-3">
                  <div className="col-md-3 col-6">
                    <small className="text-muted d-flex align-items-center gap-1 mb-1">
                      <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>bolt</span>
                      {t("Execution Type")}
                    </small>
                    <span className={`badge ${isReactive ? "bg-info text-dark" : "bg-warning text-dark"}`}>
                      {isReactive ? t("Reactive") : t("Proactive")}
                    </span>
                  </div>

                  <div className="col-md-3 col-6">
                    <small className="text-muted d-flex align-items-center gap-1 mb-1">
                      <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>list_alt</span>
                      {t("Subtasks")}
                    </small>
                    <span className="badge bg-primary">{childTasks.length} {t("tasks")}</span>
                  </div>

                  {task.moderator && (
                    <div className="col-md-3 col-6">
                      <small className="text-muted d-flex align-items-center gap-1 mb-1">
                        <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>person</span>
                        {t("Moderator")}
                      </small>
                      <span className="fw-semibold text-dark" style={{ fontSize: "13px" }}>
                        {task.moderator.name} {task.moderator.surname}
                      </span>
                    </div>
                  )}

                  <div className="col-md-3 col-6">
                    <small className="text-muted d-flex align-items-center gap-1 mb-1">
                      <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>calendar_today</span>
                      {t("Created")}
                    </small>
                    <span className="fw-semibold text-dark" style={{ fontSize: "13px" }}>
                      {new Date(task.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* ── 2. Automation Config ── */}
              <div className="mb-4 border rounded-3 overflow-hidden shadow-sm">
                <div
                  className="px-4 py-3 border-bottom d-flex align-items-center gap-2"
                  style={{ background: isReactive ? "rgba(13,202,240,0.08)" : "rgba(255,193,7,0.08)" }}
                >
                  <span className="material-symbols-outlined text-secondary" style={{ fontSize: "20px" }}>
                    settings_ethernet
                  </span>
                  <h6 className="mb-0 fw-semibold text-dark">
                    {isReactive ? t("Webhook Config") : t("Payload Config")}
                  </h6>
                  <small className="text-muted">
                    {isReactive
                      ? `— ${t("payload our server sends to n8n")}`
                      : `— ${t("data our API returns to n8n's CRON")}`}
                  </small>
                  <span
                    className={`badge ms-auto ${isReactive ? "bg-info text-dark" : "bg-warning text-dark"}`}
                    style={{ fontSize: "10px" }}
                  >
                    {isReactive ? "POST" : "GET"}
                  </span>
                </div>

                <div className="row g-0">
                  {/* Left: config fields */}
                  <div className="col-xl-6 p-4 border-end">
                    {isReactive && automationConfig ? (
                      <>
                        {/* Token */}
                        <div className="mb-3">
                          <label className="form-label fs-sm fw-semibold text-dark mb-1 d-flex align-items-center gap-1">
                            <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>key</span>
                            {t("Token")}
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-sm font-monospace bg-light"
                            value={automationConfig.token || "—"}
                            readOnly
                            style={{ fontSize: "11px", color: "#555" }}
                          />
                          <small className="text-muted">
                            {t("Used to verify n8n callbacks.")}
                          </small>
                        </div>

                        {/* Webhook URL */}
                        <div className="mb-3">
                          <label className="form-label fs-sm fw-semibold text-dark mb-1 d-flex align-items-center gap-1">
                            <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>link</span>
                            {t("Webhook URL")}
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-sm bg-light"
                            value={automationConfig.webhook_url || "—"}
                            readOnly
                            style={{ fontSize: "12px", color: "#555" }}
                          />
                        </div>

                        {/* Webhook Name */}
                        {automationConfig.webhook_name && (
                          <div className="mb-3">
                            <label className="form-label fs-sm fw-semibold text-dark mb-1 d-flex align-items-center gap-1">
                              <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>label</span>
                              {t("Webhook Name")}
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm bg-light"
                              value={automationConfig.webhook_name}
                              readOnly
                              style={{ fontSize: "12px", color: "#555" }}
                            />
                          </div>
                        )}
                      </>
                    ) : !isReactive ? (
                      /* Proactive: show copyable API endpoint */
                      <div className="p-3 rounded-2 border" style={{ background: "#fff8e1" }}>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <span className="material-symbols-outlined text-warning" style={{ fontSize: "18px" }}>api</span>
                          <p className="mb-0 fw-semibold text-dark" style={{ fontSize: "13px" }}>
                            {t("n8n CRON polls this endpoint to get pending tasks:")}
                          </p>
                          <span className="badge bg-warning text-dark ms-auto" style={{ fontSize: "10px" }}>GET</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <code
                            className="flex-grow-1 p-2 rounded-2 border"
                            style={{
                              background: "#1e1e2e",
                              color: "#cdd6f4",
                              fontSize: "12px",
                              wordBreak: "break-all",
                              display: "block",
                              lineHeight: "1.8",
                            }}
                          >
                            {proactiveUrl}
                          </code>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary flex-shrink-0"
                            title={t("Copy URL")}
                            onClick={() => navigator.clipboard?.writeText(proactiveUrl)}
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: "18px" }}>
                              content_copy
                            </span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-muted fst-italic" style={{ fontSize: "13px" }}>
                        {t("No automation config found.")}
                      </p>
                    )}

                    {/* Extra Payload Fields */}
                    {automationConfig?.payload_template &&
                      typeof automationConfig.payload_template === "object" &&
                      Object.keys(automationConfig.payload_template).length > 0 && (
                        <div className="mt-3">
                          <label className="form-label fs-sm fw-semibold text-dark mb-1">
                            {isReactive ? t("Extra Payload Fields") : t("Payload")}
                          </label>
                          <pre
                            className="rounded-2 p-2 mb-0 bg-light font-monospace"
                            style={{ fontSize: "11px", color: "#555", maxHeight: "120px", overflowY: "auto" }}
                          >
                            {JSON.stringify(automationConfig.payload_template, null, 2)}
                          </pre>
                        </div>
                      )}
                  </div>

                  {/* Right: payload preview */}
                  <div className="col-xl-6 p-4 d-flex flex-column" style={{ background: "#fafafa" }}>
                    <label className="form-label fs-sm fw-semibold d-flex align-items-center gap-2 mb-2">
                      <span className="material-symbols-outlined text-primary" style={{ fontSize: "16px" }}>
                        preview
                      </span>
                      {t("Payload Preview")}
                      <span
                        className={`badge ms-1 ${isReactive ? "bg-success" : "bg-primary"}`}
                        style={{ fontSize: "10px" }}
                      >
                        {isReactive ? "POST" : "GET"}
                      </span>
                    </label>

                    {previewJson ? (
                      <pre
                        className="rounded-3 p-3 mb-2 flex-grow-1"
                        style={{
                          background: "#1e1e2e",
                          color: "#cdd6f4",
                          fontSize: "11.5px",
                          lineHeight: "1.6",
                          maxHeight: "300px",
                          overflowY: "auto",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-all",
                        }}
                      >
                        {previewJson}
                      </pre>
                    ) : (
                      <div className="text-center py-3 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                        <small>{t("No payload configured")}</small>
                      </div>
                    )}

                    <small className="text-muted d-flex align-items-center gap-1 mt-2">
                      <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>info</span>
                      <code>{"{{taskid}}"}</code>{", "}
                      <code>{"{{email}}"}</code>{", "}
                      <code>{"{{name}}"}</code>{" "}
                      {t("are replaced with real lead data at send time.")}
                    </small>
                  </div>
                </div>
              </div>

              {/* ── 3. Child Tasks (Leads) ── */}
              <div>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="material-symbols-outlined text-primary" style={{ fontSize: "20px" }}>
                    group
                  </span>
                  <h6 className="mb-0 fw-bold text-dark">{t("Leads / Subtasks")}</h6>
                  <span className="badge bg-secondary ms-auto">
                    {childTasks.length} {t("total")}
                  </span>
                </div>

                {/* Search */}
                <div className="mb-3">
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0">
                      <span className="material-symbols-outlined text-muted" style={{ fontSize: "18px" }}>
                        search
                      </span>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0 ps-0"
                      placeholder={t("Search by lead name, email, or company...")}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{ fontSize: "14px" }}
                    />
                    {searchQuery && (
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => setSearchQuery("")}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: "16px" }}>close</span>
                      </button>
                    )}
                  </div>
                  {searchQuery && (
                    <small className="text-muted mt-1 d-block">
                      {filteredChildTasks.length} {t("of")} {childTasks.length} {t("tasks")}
                    </small>
                  )}
                </div>

                {/* Table */}
                <div className="rounded-3 border overflow-hidden shadow-sm">
                  <table className="table table-hover align-middle mb-0">
                    <thead>
                      <tr style={{ background: "#f8f9fa" }}>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "11px 14px", color: "#495057" }}>
                          #
                        </th>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "11px 14px", color: "#495057", minWidth: "200px" }}>
                          {t("Lead")}
                        </th>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "11px 14px", color: "#495057" }}>
                          {t("Schedule Date")}
                        </th>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "11px 14px", color: "#495057" }}>
                          {t("Schedule Time")}
                        </th>
                        <th style={{ fontSize: "12px", fontWeight: "600", padding: "11px 14px", color: "#495057" }}>
                          {t("Status")}
                        </th>
                        {isReactive && (
                          <th style={{ fontSize: "12px", fontWeight: "600", padding: "11px 14px", color: "#495057" }}>
                            {t("n8n Execution ID")}
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredChildTasks.length === 0 ? (
                        <tr>
                          <td
                            colSpan={isReactive ? 6 : 5}
                            className="text-center py-5"
                          >
                            <span className="material-symbols-outlined text-muted d-block" style={{ fontSize: "40px" }}>
                              inbox
                            </span>
                            <span className="text-muted" style={{ fontSize: "14px" }}>
                              {searchQuery ? t("No tasks match your search") : t("No subtasks found")}
                            </span>
                          </td>
                        </tr>
                      ) : (
                        filteredChildTasks.map((ct, idx) => {
                          const badge = getStatusBadge(ct.completedpercent);
                          return (
                            <tr key={ct._id || idx}>
                              <td style={{ padding: "11px 14px" }}>
                                <span
                                  className="badge bg-light text-dark border"
                                  style={{ fontSize: "11px", fontWeight: "600" }}
                                >
                                  {ct.customid || idx + 1}
                                </span>
                              </td>

                              <td style={{ padding: "11px 14px" }}>
                                {ct.leadid ? (
                                  <div>
                                    <div className="fw-semibold text-dark" style={{ fontSize: "13px" }}>
                                      {ct.leadid.name} {ct.leadid.surname}
                                    </div>
                                    {ct.leadid.email && (
                                      <div className="text-muted d-flex align-items-center gap-1" style={{ fontSize: "12px" }}>
                                        <span className="material-symbols-outlined" style={{ fontSize: "12px" }}>mail</span>
                                        {ct.leadid.email}
                                      </div>
                                    )}
                                    {ct.leadid.company && (
                                      <div className="text-secondary d-flex align-items-center gap-1" style={{ fontSize: "12px" }}>
                                        <span className="material-symbols-outlined" style={{ fontSize: "12px" }}>business</span>
                                        {ct.leadid.company}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>

                              <td style={{ padding: "11px 14px" }}>
                                <div className="d-flex align-items-center gap-1">
                                  <span className="material-symbols-outlined text-muted" style={{ fontSize: "14px" }}>
                                    calendar_today
                                  </span>
                                  <span style={{ fontSize: "13px" }}>
                                    {ct.startdate || "—"}
                                  </span>
                                </div>
                              </td>

                              <td style={{ padding: "11px 14px" }}>
                                <div className="d-flex align-items-center gap-1">
                                  <span className="material-symbols-outlined text-muted" style={{ fontSize: "14px" }}>
                                    schedule
                                  </span>
                                  <span style={{ fontSize: "13px" }}>
                                    {ct.starttime || "—"}
                                  </span>
                                </div>
                              </td>

                              <td style={{ padding: "11px 14px" }}>
                                <span className={`badge ${badge.color}`} style={{ fontSize: "11px" }}>
                                  {t(badge.label)}
                                </span>
                              </td>

                              {isReactive && (
                                <td style={{ padding: "11px 14px" }}>
                                  {ct.automation?.n8n_execution_id ? (
                                    <code style={{ fontSize: "11px", color: "#6c757d" }}>
                                      {ct.automation.n8n_execution_id}
                                    </code>
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          ) : (
            <div className="text-center py-5">
              <span className="material-symbols-outlined text-muted" style={{ fontSize: "64px" }}>
                error_outline
              </span>
              <p className="text-muted mt-3">{t("Failed to load task details")}</p>
              <button className="btn btn-outline-primary btn-sm" onClick={fetchDetails}>
                {t("Retry")}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Backdrop */}
      <div className="offcanvas-backdrop fade show" onClick={handleClose} />
    </>
  );
};

export default AutomationTaskDetailPopup;
